import { openWindow, closeWindow } from "../Slices/windowsSlice";

/**
 * webtopBridge — Redux middleware that lets the EXISTING screens work inside the
 * Webtop window manager WITHOUT any edits to their code.
 *
 * The legacy screens open detail pages with `dispatch(openTab(...))` and close
 * them with `dispatch(closeTab(activeTabId))`. When the app is running in the
 * Webtop shell, we transparently retarget those to the window manager:
 *   tabs/openTab   → windows/openWindow
 *   tabs/closeTab  → windows/closeWindow(focusedWindow)
 *
 * This keeps WEBTOP_SPEC §2 ("features never change, screens run unchanged")
 * true and means all 23 modules become window-aware for free — no per-screen
 * `useAppNavigate` refactor needed (and no Frontend2.0 divergence; Rule 1/4).
 *
 * Mode detection: the outer HashRouter's hash is `#/webtop` whenever the Webtop
 * surface is mounted, regardless of the inner per-window MemoryRouter paths.
 * At cutover (when the Webtop becomes the only shell) this gate can simply
 * become "always on".
 */

// Works for both HashRouter (Desktop: #/webtop) and BrowserRouter (web: /webtop).
export const isWebtopActive = () => {
  if (typeof window === "undefined") return false;
  const hash = String(window.location?.hash || "").replace(/^#/, "");
  const path = String(window.location?.pathname || "");
  return hash.startsWith("/webtop") || path.startsWith("/webtop");
};

export const webtopBridge = (store) => (next) => (action) => {
  if (!action || !isWebtopActive()) return next(action);

  if (action.type === "tabs/openTab") {
    // Same payload shape ({ path, title, deduplicate }) — openWindow accepts it.
    return next(openWindow(action.payload));
  }

  if (action.type === "tabs/closeTab") {
    // The detail page being closed is the focused window.
    const focusedId = store.getState().windows?.focusedId;
    if (focusedId) return next(closeWindow(focusedId));
    return undefined; // nothing focused → swallow the legacy tab close
  }

  return next(action);
};

export default webtopBridge;
